"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { AlertCircle, FileText, Loader2 } from "lucide-react"
import { useEditorStore, type SynctexForwardBox } from "@/lib/editor-store"
import { compilePdfTsLog, parsePdfCacheBust } from "@/lib/compiler/compile-pdf-ts-log"
import { patchPdfViewerState, clearPdfViewerState } from "@/lib/pdf-viewer-state-store"

async function fetchInverseSync(
  sessionId: string,
  page: number,
  x: number,
  y: number,
): Promise<{ line: number; column: number; word?: string } | null> {
  try {
    const url = `/api/compiler/sessions/${encodeURIComponent(sessionId)}/synctex/inverse?page=${page}&x=${x}&y=${y}`
    const res = await fetch(url)
    if (!res.ok) return null
    const data: unknown = await res.json()
    if (!data || typeof data !== "object") return null
    const o = data as Record<string, unknown>
    const line = Number(o.line)
    // Backend returns 0-based col; Monaco needs 1-based column
    const col = Number(o.col ?? o.column)
    const word = typeof o.word === "string" ? o.word : undefined
    if (!Number.isFinite(line) || line < 1) return null
    return {
      line: Math.trunc(line),
      column: Number.isFinite(col) ? Math.trunc(col) + 1 : 1,
      word
    }
  } catch {
    return null
  }
}

export type SynctexPdfViewerProps = {
  pdfUrl: string | null | undefined
  tabId: string
}

type OverlayState =
  | { kind: "spinner"; title: string; sub: string }
  | { kind: "idle";    title: string; sub: string }
  | { kind: "error";   title: string; sub?: string }
  | null

function computeOverlay(
  sessionId: string | undefined,
  sessionPhase: string,
  wsStatus: string,
  compileStatus: string,
  compileError: string | undefined,
  pdfUrl: string | null | undefined,
  frameReady: boolean,
): OverlayState {
  if (!sessionId) {
    if (sessionPhase === "loading") {
      return { kind: "spinner", title: "Starting session…", sub: "Connecting to compiler" }
    }
    return { kind: "idle", title: "No active session", sub: "Open or create a project to begin" }
  }

  if (wsStatus === "reconnecting") {
    return { kind: "spinner", title: "Reconnecting…", sub: "Trying to reach the compiler" }
  }

  if (compileStatus === "compiling") {
    return { kind: "spinner", title: "Compiling…", sub: "Building your PDF, please wait" }
  }

  if (compileStatus === "error" && !pdfUrl) {
    return {
      kind: "error",
      title: "Compilation failed",
      sub: compileError ?? "Check the Console panel for details",
    }
  }

  if (!pdfUrl) {
    return { kind: "idle", title: "No preview yet", sub: "Compile your document to see the PDF" }
  }

  if (!frameReady) {
    return { kind: "spinner", title: "Loading PDF viewer…", sub: "Initialising renderer" }
  }

  return null
}

export function SynctexPdfViewer({ pdfUrl, tabId }: SynctexPdfViewerProps) {
  const { state, registerSynctexPdfHandler, registerFramePostHandler, applyInverseSyncResult, setPdfThumbnail } = useEditorStore()

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [frameReady, setFrameReady] = useState(false)
  const pdfUrlRef = useRef(pdfUrl)
  const sessionIdRef = useRef(state.sessionId)

  useEffect(() => {
    sessionIdRef.current = state.sessionId
  }, [state.sessionId])

  useEffect(() => {
    pdfUrlRef.current = pdfUrl
    patchPdfViewerState(tabId, { url: pdfUrl ?? null, page: 1, pageCount: 0 })
  }, [pdfUrl, tabId])

  useEffect(() => {
    return () => clearPdfViewerState(tabId)
  }, [tabId])

  const toAbsolute = useCallback((url: string) => {
    if (url.startsWith("http://") || url.startsWith("https://")) return url
    return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`
  }, [])

  const postToFrame = useCallback((msg: object) => {
    const targetWindow = iframeRef.current?.contentWindow
    console.log("[SyncTeX:Debug] postToFrame -> Posting message to Flutter iframe:", {
      hasIframeRef: Boolean(iframeRef.current),
      hasContentWindow: Boolean(targetWindow),
      message: msg,
    })

    if (targetWindow) {
      targetWindow.postMessage(JSON.stringify(msg), "*")
    } else {
      console.warn("[SyncTeX:Debug] FAILED to post message — iframe contentWindow is null or not ready")
    }
  }, [])

  // Send PDF URL whenever both the frame is ready and the URL is available
  useEffect(() => {
    if (frameReady && pdfUrl) {
      const absolute = toAbsolute(pdfUrl)
      compilePdfTsLog("viewer:set_url", "posting SET_PDF_URL to Flutter iframe", {
        pdfUrl,
        absolute,
        pdfCacheBustMs: parsePdfCacheBust(pdfUrl),
        pdfCacheBustIso: (() => {
          const t = parsePdfCacheBust(pdfUrl)
          return t != null ? new Date(t).toISOString() : null
        })(),
        sessionId: state.sessionId ?? null,
        compileStatus: state.compileStatus,
      })
      console.log("[Compile:PDF] viewer SET_PDF_URL", {
        pdfUrl,
        absolute,
        cacheBustMs: parsePdfCacheBust(pdfUrl),
        sessionId: state.sessionId ?? null,
      })
      postToFrame({ type: "SET_PDF_URL", url: absolute })
    }
  }, [frameReady, pdfUrl, postToFrame, toAbsolute, state.sessionId, state.compileStatus])

  // Listen for messages from the Flutter iframe
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data
        if (!data?.type) return
        if (data.type === "FRAME_READY") {
          setFrameReady(true)
          if (pdfUrlRef.current) {
            iframeRef.current?.contentWindow?.postMessage(
              JSON.stringify({ type: "SET_PDF_URL", url: toAbsolute(pdfUrlRef.current) }),
              "*"
            )
          }
        } else if (data.type === "INVERSE_SYNC") {
          const sessionId = sessionIdRef.current
          if (!sessionId) return
          void fetchInverseSync(sessionId, data.page, data.x, data.y).then((result) => {
            if (result) applyInverseSyncResult(result.line, result.column, result.word)
          })
        } else if (data.type === "PDF_THUMBNAIL") {
          if (typeof data.dataUrl === "string" && pdfUrlRef.current) {
            setPdfThumbnail(pdfUrlRef.current, data.dataUrl)
          }
        } else if (data.type === "PDF_STATE") {
          if (event.source !== iframeRef.current?.contentWindow) return
          patchPdfViewerState(tabId, {
            ...(typeof data.page === "number" && { page: data.page }),
            ...(typeof data.pageCount === "number" && { pageCount: data.pageCount }),
            ...(typeof data.zoomPercent === "number" && { zoomPercent: data.zoomPercent }),
            searchMatchCount: typeof data.searchMatchCount === "number" ? data.searchMatchCount : null,
            searchCurrentIndex: typeof data.searchCurrentIndex === "number" ? data.searchCurrentIndex : null,
          })
        }
      } catch {
        // ignore malformed messages
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [toAbsolute, applyInverseSyncResult, setPdfThumbnail, tabId])

  // Register SyncTeX forward handler
  useEffect(() => {
    const unregister = registerSynctexPdfHandler((box: SynctexForwardBox) => {
      console.log("[SyncTeX:Debug] SynctexPdfViewer received box:", box)
      postToFrame({
        type: "SYNC_FORWARD",
        page: box.page,
        x: box.x,
        y: box.y,
        width: box.width,
        height: box.height,
        sourceLine: box.sourceLine,
        sourceColumn: box.sourceColumn,
        occurrenceIndex: box.occurrenceIndex,
      })
    })

    return () => {
      if (typeof unregister === "function") {
        unregister()
      } else {
        registerSynctexPdfHandler(null)
      }
    }
  }, [registerSynctexPdfHandler, postToFrame])

  // Register frame post handler (SET_SELECTED_WORD / SET_CURSOR)
  useEffect(() => {
    const unregister = registerFramePostHandler((msg) => {
      console.log("[SyncTeX:Debug] SynctexPdfViewer received frame msg from store:", msg)
      postToFrame(msg)
    })

    return () => {
      if (typeof unregister === "function") {
        unregister()
      } else {
        registerFramePostHandler(null)
      }
    }
  }, [registerFramePostHandler, postToFrame])

  const overlay = computeOverlay(
    state.sessionId,
    state.sessionPhase,
    state.wsStatus,
    state.compileStatus,
    state.compileError,
    pdfUrl,
    frameReady,
  )

  return (
    <div className="relative h-full w-full bg-white dark:bg-[var(--ed-preview-canvas-bg)]">
      {/* Overlay: covers the iframe while the viewer is not ready to show anything useful */}
      {overlay && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[var(--ed-preview-canvas-bg)] px-6 text-center">
          {overlay.kind === "spinner" && (
            <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
          )}
          {overlay.kind === "idle" && (
            <FileText className="w-8 h-8 text-[var(--ed-preview-hint-icon)]" />
          )}
          {overlay.kind === "error" && (
            <AlertCircle className="w-8 h-8 text-red-400" />
          )}

          <p className={`text-sm font-medium ${
            overlay.kind === "error"
              ? "text-[var(--ed-preview-error-text)]"
              : "text-[var(--ed-preview-text)]"
          }`}>
            {overlay.title}
          </p>

          {overlay.sub && (
            <p className="text-xs text-[var(--ed-preview-sub-text)] max-w-xs leading-relaxed">
              {overlay.sub}
            </p>
          )}
        </div>
      )}

      <div className="h-full w-full p-3">
        <iframe
          ref={iframeRef}
          src="/pdf_viewer/index.html"
          className="h-full w-full border-0 rounded-xl overflow-hidden"
          title="PDF Viewer"
        />
      </div>
    </div>
  )
}
